import * as THREE from 'three';
import { FractalType, ColorScheme } from '../data/chapters';
import { AnalysisData } from '../audio/AudioAnalyzer';

// Import shaders
import mandelbrotFrag from './shaders/mandelbrot.glsl';
import juliaFrag from './shaders/julia.glsl';
import transformFrag from './shaders/transform.glsl';
import elementsFrag from './shaders/elements.glsl';
import gradientFrag from './shaders/gradient.glsl';
import spiralFrag from './shaders/spiral.glsl';
import synthesisFrag from './shaders/synthesis.glsl';
import vertexShader from './shaders/vertex.glsl';

const shaderMap: Record<FractalType, string> = {
  mandelbrot: mandelbrotFrag,
  julia: juliaFrag,
  transform: transformFrag,
  elements: elementsFrag,
  gradient: gradientFrag,
  spiral: spiralFrag,
  synthesis: synthesisFrag
};

export class FractalRenderer {
  // @ts-ignore - container stored for potential future use
  private _container: HTMLElement;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.OrthographicCamera;
  private mesh: THREE.Mesh;
  private material: THREE.ShaderMaterial;
  
  private currentFractalType: FractalType = 'mandelbrot';
  private targetColorScheme: ColorScheme | null = null;
  private currentColorScheme: ColorScheme = {
    primary: [0.05, 0.08, 0.15],
    secondary: [0.4, 0.25, 0.1],
    accent: [0.85, 0.65, 0.45]
  };
  
  private uniforms: {
    uTime: { value: number };
    uResolution: { value: THREE.Vector2 };
    uBass: { value: number };
    uMid: { value: number };
    uHigh: { value: number };
    uAudioAverage: { value: number };
    uZoom: { value: number };
    uOffset: { value: THREE.Vector2 };
    uColorPrimary: { value: THREE.Vector3 };
    uColorSecondary: { value: THREE.Vector3 };
    uColorAccent: { value: THREE.Vector3 };
    uMorphProgress: { value: number };
    uJuliaC: { value: THREE.Vector2 };
    uDeviceTilt: { value: THREE.Vector2 };
  };
  
  private transitionProgress: number = 1;
  private transitionDuration: number = 4;  // Longer transition for smoother feel
  private transitionStartTime: number = 0;
  
  // Interactive zoom and pan
  private interactionEnabled: boolean = false;
  private interactiveZoom: number = 1.0;
  private interactiveOffset: THREE.Vector2 = new THREE.Vector2(0, 0);
  private targetZoom: number = 1.0;
  private targetOffset: THREE.Vector2 = new THREE.Vector2(0, 0);
  
  // Mouse interaction
  private isMouseDown: boolean = false;
  private isDragging: boolean = false;
  private lastMousePos: THREE.Vector2 = new THREE.Vector2(0, 0);
  private mouseDownPos: THREE.Vector2 = new THREE.Vector2(0, 0);
  
  // Touch interaction
  private lastTouchDistance: number = 0;
  private lastTouchCenter: THREE.Vector2 = new THREE.Vector2(0, 0);
  private isTouching: boolean = false;
  private touchStartTime: number = 0;
  
  // Momentum/inertia for smooth movement
  private velocity: THREE.Vector2 = new THREE.Vector2(0, 0);
  private lastDragTime: number = 0;
  private lastDragDelta: THREE.Vector2 = new THREE.Vector2(0, 0);
  
  // Double tap detection
  private lastTapTime: number = 0;
  private doubleTapDelay: number = 300;
  
  // Device orientation (mobile gyroscope)
  private deviceTilt: THREE.Vector2 = new THREE.Vector2(0, 0);
  private targetDeviceTilt: THREE.Vector2 = new THREE.Vector2(0, 0);
  private deviceOrientationEnabled: boolean = false;
  
  constructor(container: HTMLElement) {
    this._container = container;
    
    // Setup renderer
    this.renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      powerPreference: 'high-performance'
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    container.appendChild(this.renderer.domElement);
    
    // Setup scene
    this.scene = new THREE.Scene();
    
    // Setup camera (orthographic for fullscreen quad)
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    
    // Setup uniforms
    this.uniforms = {
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
      uBass: { value: 0 },
      uMid: { value: 0 },
      uHigh: { value: 0 },
      uAudioAverage: { value: 0 },
      uZoom: { value: 1.0 },
      uOffset: { value: new THREE.Vector2(0.0, 0.0) },
      uColorPrimary: { value: new THREE.Vector3(...this.currentColorScheme.primary) },
      uColorSecondary: { value: new THREE.Vector3(...this.currentColorScheme.secondary) },
      uColorAccent: { value: new THREE.Vector3(...this.currentColorScheme.accent) },
      uMorphProgress: { value: 0 },
      uJuliaC: { value: new THREE.Vector2(-0.4, 0.6) },
      uDeviceTilt: { value: new THREE.Vector2(0, 0) }
    };
    
    // Create material with initial shader
    this.material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader: mandelbrotFrag,
      uniforms: this.uniforms
    });
    
    // Create fullscreen quad
    const geometry = new THREE.PlaneGeometry(2, 2);
    this.mesh = new THREE.Mesh(geometry, this.material);
    this.scene.add(this.mesh);
    
    // Setup mouse interaction
    this.setupMouseInteraction();
    
    // Setup device orientation for mobile
    this.setupDeviceOrientation();
  }
  
  private setupDeviceOrientation(): void {
    // Check if DeviceOrientationEvent is available
    if (typeof DeviceOrientationEvent === 'undefined') {
      console.log('DeviceOrientation not supported');
      return;
    }
    
    // iOS 13+ requires permission
    if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      // Request permission on user interaction (touch or click)
      const requestPermission = async () => {
        if (this.deviceOrientationEnabled) return;
        try {
          const permission = await (DeviceOrientationEvent as any).requestPermission();
          if (permission === 'granted') {
            this.enableDeviceOrientation();
          }
        } catch (e) {
          console.log('Device orientation permission denied or error:', e);
        }
      };
      
      // Try both touch and click for better compatibility
      this.renderer.domElement.addEventListener('touchstart', requestPermission, { once: true });
      this.renderer.domElement.addEventListener('click', requestPermission, { once: true });
    } else {
      // Android, desktop, and older iOS
      this.enableDeviceOrientation();
    }
  }
  
  private enableDeviceOrientation(): void {
    if (this.deviceOrientationEnabled) return;
    this.deviceOrientationEnabled = true;
    
    // Use both standard and webkit events for compatibility
    const handleOrientation = (event: DeviceOrientationEvent) => {
      // Check for valid values (some devices return null)
      const beta = event.beta ?? 0;
      const gamma = event.gamma ?? 0;
      
      if (beta === 0 && gamma === 0) return;
      
      // beta: front-back tilt (-180 to 180), gamma: left-right tilt (-90 to 90)
      // Normalize to -1 to 1 range, with subtle influence
      const sensitivity = 0.015;
      
      // Gamma (left-right tilt) affects X
      this.targetDeviceTilt.x = (gamma / 90) * sensitivity;
      
      // Beta (front-back tilt) affects Y, centered around 45 degrees
      const betaCentered = (beta - 45) / 90;
      this.targetDeviceTilt.y = Math.max(-1, Math.min(1, betaCentered)) * sensitivity;
    };
    
    window.addEventListener('deviceorientation', handleOrientation);
    // Some older browsers use this event name
    window.addEventListener('deviceorientation' as any, handleOrientation);
  }
  
  private setupMouseInteraction(): void {
    const canvas = this.renderer.domElement;
    
    // Helper to get normalized coordinates
    const getNormalizedCoords = (clientX: number, clientY: number): THREE.Vector2 => {
      const rect = canvas.getBoundingClientRect();
      return new THREE.Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1
      );
    };
    
    // Helper to convert screen to fractal space
    const screenToFractal = (screenX: number, screenY: number, zoom: number, offsetX: number, offsetY: number): THREE.Vector2 => {
      const rect = canvas.getBoundingClientRect();
      const aspect = rect.width / rect.height;
      return new THREE.Vector2(
        (screenX * aspect) / zoom + offsetX,
        screenY / zoom + offsetY
      );
    };
    
    // ============ MOUSE EVENTS ============
    
    // Mouse down - start drag
    canvas.addEventListener('mousedown', (e) => {
      if (!this.interactionEnabled) return;
      e.preventDefault();
      
      this.isMouseDown = true;
      this.isDragging = false;
      this.velocity.set(0, 0); // Stop any momentum
      canvas.style.cursor = 'grabbing';
      const coords = getNormalizedCoords(e.clientX, e.clientY);
      this.mouseDownPos.copy(coords);
      this.lastMousePos.copy(coords);
      this.lastDragTime = performance.now();
      
      // Sync immediately with current values
      this.interactiveZoom = this.uniforms.uZoom.value;
      this.interactiveOffset.x = this.uniforms.uOffset.value.x;
      this.interactiveOffset.y = this.uniforms.uOffset.value.y;
      this.targetZoom = this.interactiveZoom;
      this.targetOffset.copy(this.interactiveOffset);
    });
    
    // Mouse move - drag to pan (immediate, no lerp during drag)
    canvas.addEventListener('mousemove', (e) => {
      if (!this.interactionEnabled || !this.isMouseDown) return;
      
      const coords = getNormalizedCoords(e.clientX, e.clientY);
      const deltaX = coords.x - this.lastMousePos.x;
      const deltaY = coords.y - this.lastMousePos.y;
      
      // Check if we've moved enough to start dragging
      const distFromStart = Math.sqrt(
        Math.pow(coords.x - this.mouseDownPos.x, 2) + 
        Math.pow(coords.y - this.mouseDownPos.y, 2)
      );
      if (distFromStart > 0.01) {
        this.isDragging = true;
      }
      
      if (this.isDragging) {
        const rect = canvas.getBoundingClientRect();
        const aspect = rect.width / rect.height;
        const currentZoom = this.interactiveZoom;
        
        // Calculate movement in fractal space
        const moveX = (deltaX * aspect) / currentZoom;
        const moveY = deltaY / currentZoom;
        
        // Apply directly to both target and current (immediate response)
        this.targetOffset.x -= moveX;
        this.targetOffset.y -= moveY;
        this.interactiveOffset.x -= moveX;
        this.interactiveOffset.y -= moveY;
        
        // Track velocity for momentum
        const now = performance.now();
        const dt = (now - this.lastDragTime) / 1000;
        if (dt > 0) {
          this.lastDragDelta.set(moveX, moveY);
          this.velocity.set(-moveX / dt * 0.3, -moveY / dt * 0.3);
        }
        this.lastDragTime = now;
      }
      
      this.lastMousePos.copy(coords);
    });
    
    // Mouse up - end drag or zoom on click
    canvas.addEventListener('mouseup', (e) => {
      if (!this.interactionEnabled) return;
      
      // If we didn't drag, treat as click to zoom
      if (!this.isDragging && this.isMouseDown) {
        const coords = getNormalizedCoords(e.clientX, e.clientY);
        const currentZoom = this.interactiveZoom;
        
        const fractalPos = screenToFractal(coords.x, coords.y, currentZoom, this.interactiveOffset.x, this.interactiveOffset.y);
        
        // Left click = zoom in, right click = zoom out
        const isZoomOut = e.button === 2;
        const zoomFactor = isZoomOut ? 0.65 : 1.5;
        const newZoom = Math.max(0.5, Math.min(currentZoom * zoomFactor, 10000));
        
        const rect = canvas.getBoundingClientRect();
        const aspect = rect.width / rect.height;
        this.targetOffset.x = fractalPos.x - (coords.x * aspect) / newZoom;
        this.targetOffset.y = fractalPos.y - coords.y / newZoom;
        this.targetZoom = newZoom;
      }
      
      this.isMouseDown = false;
      this.isDragging = false;
      if (this.interactionEnabled) {
        canvas.style.cursor = 'grab';
      }
    });
    
    canvas.addEventListener('mouseleave', () => {
      this.isMouseDown = false;
      this.isDragging = false;
      if (this.interactionEnabled) {
        canvas.style.cursor = 'grab';
      }
    });
    
    // Mouse wheel - smooth zoom centered on cursor
    canvas.addEventListener('wheel', (e) => {
      if (!this.interactionEnabled) return;
      e.preventDefault();
      
      const coords = getNormalizedCoords(e.clientX, e.clientY);
      const currentZoom = this.interactiveZoom;
      
      const fractalPos = screenToFractal(coords.x, coords.y, currentZoom, this.interactiveOffset.x, this.interactiveOffset.y);
      
      // Smooth zoom factor based on scroll direction and current zoom level
      const baseScrollFactor = e.deltaY > 0 ? 0.88 : 1.14;
      // Adaptive: at high zoom, smaller steps
      const adaptiveFactor = 1 + (baseScrollFactor - 1) / Math.pow(1 + Math.log10(currentZoom + 1) * 0.3, 1);
      const newZoom = Math.max(0.5, Math.min(currentZoom * adaptiveFactor, 10000));
      
      const rect = canvas.getBoundingClientRect();
      const aspect = rect.width / rect.height;
      this.targetOffset.x = fractalPos.x - (coords.x * aspect) / newZoom;
      this.targetOffset.y = fractalPos.y - coords.y / newZoom;
      this.targetZoom = newZoom;
    }, { passive: false });
    
    // Prevent context menu
    canvas.addEventListener('contextmenu', (e) => {
      if (this.interactionEnabled) {
        e.preventDefault();
      }
    });
    
    // ============ TOUCH EVENTS ============
    
    // Touch start
    canvas.addEventListener('touchstart', (e) => {
      if (!this.interactionEnabled) return;
      e.preventDefault();
      
      this.touchStartTime = Date.now();
      this.isTouching = true;
      this.velocity.set(0, 0); // Stop any momentum
      this.isDragging = false;
      
      // Sync with current values
      this.interactiveZoom = this.uniforms.uZoom.value;
      this.interactiveOffset.x = this.uniforms.uOffset.value.x;
      this.interactiveOffset.y = this.uniforms.uOffset.value.y;
      this.targetZoom = this.interactiveZoom;
      this.targetOffset.copy(this.interactiveOffset);
      this.lastDragTime = performance.now();
      
      if (e.touches.length === 1) {
        // Single touch - prepare for drag
        const touch = e.touches[0];
        const coords = getNormalizedCoords(touch.clientX, touch.clientY);
        this.lastTouchCenter.copy(coords);
        this.mouseDownPos.copy(coords);
      } else if (e.touches.length === 2) {
        // Pinch - calculate initial distance and center
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        
        this.lastTouchDistance = Math.sqrt(
          Math.pow(t2.clientX - t1.clientX, 2) + 
          Math.pow(t2.clientY - t1.clientY, 2)
        );
        
        const centerX = (t1.clientX + t2.clientX) / 2;
        const centerY = (t1.clientY + t2.clientY) / 2;
        this.lastTouchCenter = getNormalizedCoords(centerX, centerY);
      }
    }, { passive: false });
    
    // Touch move - immediate drag and pinch (no lerp delay)
    canvas.addEventListener('touchmove', (e) => {
      if (!this.interactionEnabled || !this.isTouching) return;
      e.preventDefault();
      
      const rect = canvas.getBoundingClientRect();
      const aspect = rect.width / rect.height;
      
      if (e.touches.length === 1) {
        // Single finger drag - immediate response
        const touch = e.touches[0];
        const coords = getNormalizedCoords(touch.clientX, touch.clientY);
        
        const deltaX = coords.x - this.lastTouchCenter.x;
        const deltaY = coords.y - this.lastTouchCenter.y;
        
        // Start dragging immediately with smaller threshold
        const distFromStart = Math.sqrt(
          Math.pow(coords.x - this.mouseDownPos.x, 2) + 
          Math.pow(coords.y - this.mouseDownPos.y, 2)
        );
        if (distFromStart > 0.015) {
          this.isDragging = true;
        }
        
        if (this.isDragging) {
          const currentZoom = this.interactiveZoom;
          const moveX = (deltaX * aspect) / currentZoom;
          const moveY = deltaY / currentZoom;
          
          // Apply directly for immediate response
          this.targetOffset.x -= moveX;
          this.targetOffset.y -= moveY;
          this.interactiveOffset.x -= moveX;
          this.interactiveOffset.y -= moveY;
          
          // Track velocity for momentum
          const now = performance.now();
          const dt = (now - this.lastDragTime) / 1000;
          if (dt > 0) {
            this.velocity.set(-moveX / dt * 0.25, -moveY / dt * 0.25);
          }
          this.lastDragTime = now;
        }
        
        this.lastTouchCenter.copy(coords);
        
      } else if (e.touches.length === 2) {
        // Pinch zoom + two-finger drag - immediate response
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        
        // Calculate new distance
        const newDistance = Math.sqrt(
          Math.pow(t2.clientX - t1.clientX, 2) + 
          Math.pow(t2.clientY - t1.clientY, 2)
        );
        
        // Calculate new center
        const centerX = (t1.clientX + t2.clientX) / 2;
        const centerY = (t1.clientY + t2.clientY) / 2;
        const newCenter = getNormalizedCoords(centerX, centerY);
        
        // Zoom based on pinch - immediate
        if (this.lastTouchDistance > 0) {
          const zoomDelta = newDistance / this.lastTouchDistance;
          const currentZoom = this.interactiveZoom;
          
          // Get fractal position at pinch center
          const fractalPos = screenToFractal(
            newCenter.x, newCenter.y,
            currentZoom,
            this.interactiveOffset.x,
            this.interactiveOffset.y
          );
          
          const newZoom = Math.max(0.5, Math.min(currentZoom * zoomDelta, 10000));
          
          // Adjust offset to keep pinch center in place - apply immediately
          const newOffsetX = fractalPos.x - (newCenter.x * aspect) / newZoom;
          const newOffsetY = fractalPos.y - newCenter.y / newZoom;
          
          this.targetOffset.x = newOffsetX;
          this.targetOffset.y = newOffsetY;
          this.targetZoom = newZoom;
          this.interactiveOffset.x = newOffsetX;
          this.interactiveOffset.y = newOffsetY;
          this.interactiveZoom = newZoom;
        }
        
        // Two-finger drag - immediate
        const deltaX = newCenter.x - this.lastTouchCenter.x;
        const deltaY = newCenter.y - this.lastTouchCenter.y;
        const currentZoom = this.interactiveZoom;
        const moveX = (deltaX * aspect) / currentZoom;
        const moveY = deltaY / currentZoom;
        this.targetOffset.x -= moveX;
        this.targetOffset.y -= moveY;
        this.interactiveOffset.x -= moveX;
        this.interactiveOffset.y -= moveY;
        
        this.lastTouchDistance = newDistance;
        this.lastTouchCenter.copy(newCenter);
        this.isDragging = true;
      }
    }, { passive: false });
    
    // Touch end - double tap detection
    canvas.addEventListener('touchend', (e) => {
      if (!this.interactionEnabled) return;
      
      const now = Date.now();
      const tapDuration = now - this.touchStartTime;
      
      // Quick tap detection (not drag)
      if (!this.isDragging && tapDuration < 250 && e.changedTouches.length === 1) {
        const touch = e.changedTouches[0];
        const coords = getNormalizedCoords(touch.clientX, touch.clientY);
        const currentZoom = this.interactiveZoom;
        const rect = canvas.getBoundingClientRect();
        const aspect = rect.width / rect.height;
        
        const fractalPos = screenToFractal(coords.x, coords.y, currentZoom, this.interactiveOffset.x, this.interactiveOffset.y);
        
        // Check for double tap
        if (now - this.lastTapTime < this.doubleTapDelay) {
          // Double tap - zoom OUT
          const zoomFactor = 0.5;
          const newZoom = Math.max(0.5, currentZoom * zoomFactor);
          
          this.targetOffset.x = fractalPos.x - (coords.x * aspect) / newZoom;
          this.targetOffset.y = fractalPos.y - coords.y / newZoom;
          this.targetZoom = newZoom;
          this.lastTapTime = 0; // Reset
        } else {
          // Single tap - zoom IN
          const zoomFactor = 1.6;
          const newZoom = Math.min(currentZoom * zoomFactor, 10000);
          
          this.targetOffset.x = fractalPos.x - (coords.x * aspect) / newZoom;
          this.targetOffset.y = fractalPos.y - coords.y / newZoom;
          this.targetZoom = newZoom;
          this.lastTapTime = now;
        }
      }
      
      if (e.touches.length === 0) {
        this.isTouching = false;
        this.isDragging = false;
        this.lastTouchDistance = 0;
      } else if (e.touches.length === 1) {
        // Switched from 2 fingers to 1
        const touch = e.touches[0];
        this.lastTouchCenter = getNormalizedCoords(touch.clientX, touch.clientY);
        this.lastTouchDistance = 0;
        this.isDragging = false;
      }
    }, { passive: false });
  }
  
  public enableInteraction(): void {
    this.interactionEnabled = true;
    this.renderer.domElement.style.cursor = 'grab';
    
    // Sync interactive values with current uniform values (no jump)
    this.interactiveZoom = this.uniforms.uZoom.value;
    this.targetZoom = this.uniforms.uZoom.value;
    this.interactiveOffset.x = this.uniforms.uOffset.value.x;
    this.interactiveOffset.y = this.uniforms.uOffset.value.y;
    this.targetOffset.x = this.uniforms.uOffset.value.x;
    this.targetOffset.y = this.uniforms.uOffset.value.y;
  }
  
  public disableInteraction(): void {
    this.interactionEnabled = false;
    this.renderer.domElement.style.cursor = 'default';
    this.isMouseDown = false;
    this.isDragging = false;
    this.isTouching = false;
  }
  
  public resetInteractiveZoom(): void {
    // Smooth reset to default over time
    // Don't jump - just set targets, the lerp will handle smooth transition
    this.targetZoom = 1.0;
    this.targetOffset.set(0, 0);
    
    // Sync current values if not already close
    if (Math.abs(this.interactiveZoom - 1.0) > 0.1 || 
        Math.abs(this.interactiveOffset.x) > 0.1 ||
        Math.abs(this.interactiveOffset.y) > 0.1) {
      this.interactiveZoom = this.uniforms.uZoom.value;
      this.interactiveOffset.x = this.uniforms.uOffset.value.x;
      this.interactiveOffset.y = this.uniforms.uOffset.value.y;
    }
  }
  
  public setFractalType(type: FractalType, colorScheme: ColorScheme): void {
    if (type !== this.currentFractalType) {
      this.currentFractalType = type;
      
      // Update shader
      this.material.fragmentShader = shaderMap[type];
      this.material.needsUpdate = true;
      
      // Start color transition
      this.targetColorScheme = colorScheme;
      this.transitionProgress = 0;
      this.transitionStartTime = this.uniforms.uTime.value;
    }
  }
  
  public crossfadeToFractal(type: FractalType, colorScheme: ColorScheme, durationMs: number = 3000): void {
    const canvas = this.renderer.domElement;
    const halfDuration = durationMs / 2;
    const needsShaderChange = type !== this.currentFractalType;
    
    // Phase 1: Fade out current fractal
    canvas.style.transition = `opacity ${halfDuration}ms ease-in-out`;
    canvas.style.opacity = '0.15'; // Don't go fully black, keep it subtle
    
    // At midpoint, switch fractal and colors
    setTimeout(() => {
      // Only change shader if type is different
      if (needsShaderChange) {
        this.currentFractalType = type;
        this.material.fragmentShader = shaderMap[type];
        this.material.needsUpdate = true;
      }
      
      // Always update colors
      this.targetColorScheme = colorScheme;
      this.transitionProgress = 0;
      this.transitionStartTime = this.uniforms.uTime.value;
      
      // Phase 2: Fade in new fractal
      canvas.style.transition = `opacity ${halfDuration}ms ease-in-out`;
      canvas.style.opacity = '1';
    }, halfDuration);
  }
  
  public update(time: number, audioData: AnalysisData): void {
    this.uniforms.uTime.value = time;
    
    // Update audio uniforms with smooth interpolation
    // Amplify audio values for stronger reactivity (2.5x boost)
    const audioBoost = 2.5;
    const boostedBass = Math.min(audioData.bass * audioBoost, 1.0);
    const boostedMid = Math.min(audioData.mid * audioBoost, 1.0);
    const boostedHigh = Math.min(audioData.high * audioBoost, 1.0);
    const boostedAverage = Math.min(audioData.average * audioBoost, 1.0);
    
    // Faster response (0.15 instead of 0.1)
    this.uniforms.uBass.value = this.lerp(this.uniforms.uBass.value, boostedBass, 0.15);
    this.uniforms.uMid.value = this.lerp(this.uniforms.uMid.value, boostedMid, 0.15);
    this.uniforms.uHigh.value = this.lerp(this.uniforms.uHigh.value, boostedHigh, 0.15);
    this.uniforms.uAudioAverage.value = this.lerp(this.uniforms.uAudioAverage.value, boostedAverage, 0.15);
    
    // Smooth device tilt interpolation
    this.deviceTilt.x = this.lerp(this.deviceTilt.x, this.targetDeviceTilt.x, 0.08);
    this.deviceTilt.y = this.lerp(this.deviceTilt.y, this.targetDeviceTilt.y, 0.08);
    this.uniforms.uDeviceTilt.value.set(this.deviceTilt.x, this.deviceTilt.y);
    
    // Handle interactive zoom
    if (this.interactionEnabled) {
      // Apply momentum when not actively dragging
      if (!this.isDragging && !this.isTouching) {
        // Decay momentum
        const friction = 0.95;
        this.velocity.x *= friction;
        this.velocity.y *= friction;
        
        // Apply velocity to offset
        if (Math.abs(this.velocity.x) > 0.0001 || Math.abs(this.velocity.y) > 0.0001) {
          this.targetOffset.x += this.velocity.x * 0.016;
          this.targetOffset.y += this.velocity.y * 0.016;
        }
      }
      
      // Smooth interpolation - faster during active interaction, smoother after
      const isActive = this.isDragging || this.isTouching || this.isMouseDown;
      const lerpSpeed = isActive ? 0.5 : 0.18; // Much faster during drag
      
      this.interactiveZoom = this.lerp(this.interactiveZoom, this.targetZoom, lerpSpeed);
      this.interactiveOffset.x = this.lerp(this.interactiveOffset.x, this.targetOffset.x, lerpSpeed);
      this.interactiveOffset.y = this.lerp(this.interactiveOffset.y, this.targetOffset.y, lerpSpeed);
      
      // Clamp zoom
      this.interactiveZoom = Math.max(0.5, Math.min(this.interactiveZoom, 10000));
      
      // Apply interactive zoom to uniforms
      this.uniforms.uZoom.value = this.interactiveZoom;
      this.uniforms.uOffset.value.set(this.interactiveOffset.x, this.interactiveOffset.y);
    } else {
      // When interaction is disabled, DON'T reset zoom
      // Keep the user's position and just apply uniforms
      this.uniforms.uZoom.value = this.interactiveZoom;
      this.uniforms.uOffset.value.set(this.interactiveOffset.x, this.interactiveOffset.y);
      
      // Subtle automatic animation (optional - can disable if feels weird)
      // Just add small audio reactivity, don't change zoom drastically
      const bass = this.uniforms.uBass.value;
      const subtleZoomPulse = 1 + bass * 0.02; // Very subtle
      this.uniforms.uZoom.value *= subtleZoomPulse;
    }
    
    // Update Julia set parameter for julia shader
    if (this.currentFractalType === 'julia') {
      this.updateJuliaParameter(time);
    }
    
    // Handle color transition
    if (this.targetColorScheme && this.transitionProgress < 1) {
      const elapsed = time - this.transitionStartTime;
      this.transitionProgress = Math.min(elapsed / this.transitionDuration, 1);
      const eased = this.easeInOutCubic(this.transitionProgress);
      
      // Interpolate colors
      this.currentColorScheme.primary = this.lerpColor(
        this.currentColorScheme.primary,
        this.targetColorScheme.primary,
        eased * 0.1
      );
      this.currentColorScheme.secondary = this.lerpColor(
        this.currentColorScheme.secondary,
        this.targetColorScheme.secondary,
        eased * 0.1
      );
      this.currentColorScheme.accent = this.lerpColor(
        this.currentColorScheme.accent,
        this.targetColorScheme.accent,
        eased * 0.1
      );
      
      this.uniforms.uColorPrimary.value.set(...this.currentColorScheme.primary);
      this.uniforms.uColorSecondary.value.set(...this.currentColorScheme.secondary);
      this.uniforms.uColorAccent.value.set(...this.currentColorScheme.accent);
    }
  }
  
  private updateZoom(time: number): void {
    const bass = this.uniforms.uBass.value;
    
    switch (this.currentFractalType) {
      case 'mandelbrot':
        // Slow zoom towards edge with subtle pulsing
        const baseZoom = 1.0 + time * 0.02;
        this.uniforms.uZoom.value = baseZoom * (1 + bass * 0.05);
        this.uniforms.uOffset.value.set(
          -0.745 + Math.sin(time * 0.1) * 0.001,
          0.186 + Math.cos(time * 0.1) * 0.001
        );
        break;
        
      case 'julia':
        this.uniforms.uZoom.value = 1.5 + Math.sin(time * 0.3) * 0.3 + bass * 0.2;
        break;
        
      case 'transform':
        this.uniforms.uZoom.value = 2.0 + Math.sin(time * 0.5) * 0.5;
        break;
        
      case 'elements':
        this.uniforms.uZoom.value = 1.8 + bass * 0.4;
        break;
        
      case 'gradient':
        this.uniforms.uZoom.value = 1.0 + time * 0.01;
        break;
        
      case 'spiral':
        this.uniforms.uZoom.value = 1.5 + Math.sin(time * 0.2) * 0.5;
        this.uniforms.uOffset.value.set(
          Math.sin(time * 0.1) * 0.2,
          Math.cos(time * 0.1) * 0.2
        );
        break;
        
      case 'synthesis':
        this.uniforms.uZoom.value = 1.2 + Math.sin(time * 0.15) * 0.3;
        break;
    }
  }
  
  private updateJuliaParameter(time: number): void {
    // Morph Julia set parameter over time
    const angle = time * 0.1;
    const radius = 0.7885 + Math.sin(time * 0.05) * 0.1;
    this.uniforms.uJuliaC.value.set(
      Math.cos(angle) * radius,
      Math.sin(angle) * radius
    );
  }
  
  public render(): void {
    this.renderer.render(this.scene, this.camera);
  }
  
  public resize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    this.renderer.setSize(width, height);
    this.uniforms.uResolution.value.set(width, height);
  }
  
  public destroy(): void {
    this.renderer.dispose();
    this.material.dispose();
    this.mesh.geometry.dispose();
  }
  
  private lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
  }
  
  private lerpColor(
    a: [number, number, number], 
    b: [number, number, number], 
    t: number
  ): [number, number, number] {
    return [
      this.lerp(a[0], b[0], t),
      this.lerp(a[1], b[1], t),
      this.lerp(a[2], b[2], t)
    ];
  }
  
  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
}

